// Feature: features/nma-appeal-templates-demo-ui.md
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Input,
  Textarea,
  Button,
  Checkbox,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  HStack,
  VStack,
  Divider,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  AttachmentIcon,
  AddIcon,
  RepeatIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {
  PROCEDURE_TYPE_OPTIONS,
  SUBMISSION_CHANNEL_OPTIONS,
  matchTemplatesForClaim,
  hydrateTemplateText,
  buildAppealJobCustomInstructions,
} from './appealTemplatesStore';
import { formatCurrency } from './shared';

const NMAAppealLaunchModal = ({
  isOpen,
  onClose,
  claimRow,
  templates = [],
  savedSelection = null,
  isSubmitting = false,
  forceRegenerate = false,
  onConfirmLaunch,
  onSwitchToTemplatesTab,
}) => {
  const toast = useToast();
  const extraFileInputRef = useRef(null);

  const [selectedProcedure, setSelectedProcedure] = useState('');
  const [submissionChannel, setSubmissionChannel] = useState('STANDARD_APPEAL_PACKET');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [addTemplateDropdownId, setAddTemplateDropdownId] = useState('');
  const [compiledText, setCompiledText] = useState('');
  const [isTextManuallyEdited, setIsTextManuallyEdited] = useState(false);
  const [billerNotes, setBillerNotes] = useState('');
  const [extraFiles, setExtraFiles] = useState([]);

  // Run the intelligent matcher whenever claimRow, templates, or selectedProcedure changes
  const matchResult = useMemo(() => {
    if (!claimRow) {
      return {
        inferredProcedure: {
          procedureType: PROCEDURE_TYPE_OPTIONS[2],
          shortLabel: 'Spine IONM',
          evidenceCodes: [],
          reason: '',
        },
        effectiveProcedure: PROCEDURE_TYPE_OPTIONS[2],
        recommendedChannel: 'STANDARD_APPEAL_PACKET',
        matchedTemplates: [],
        otherTemplates: [],
      };
    }
    return matchTemplatesForClaim(claimRow, templates, selectedProcedure);
  }, [claimRow, templates, selectedProcedure]);

  // Initialize modal state when opened for a claim
  useEffect(() => {
    if (!isOpen || !claimRow) return;
    const initialMatch = matchTemplatesForClaim(claimRow, templates, '');
    const initProc = savedSelection?.procedure_type || initialMatch.inferredProcedure.procedureType;
    const initChannel = savedSelection?.submission_channel || initialMatch.recommendedChannel;

    const procMatch = matchTemplatesForClaim(claimRow, templates, initProc);
    const defaultIds =
      savedSelection?.template_ids && savedSelection.template_ids.length > 0
        ? savedSelection.template_ids.filter((id) => templates.some((t) => t.id === id))
        : procMatch.matchedTemplates.map((t) => t.id);

    setSelectedProcedure(initProc);
    setSubmissionChannel(initChannel);
    setSelectedTemplateIds(
      defaultIds.length > 0 ? defaultIds : procMatch.matchedTemplates.map((t) => t.id)
    );
    setIsTextManuallyEdited(false);
    setBillerNotes(savedSelection?.biller_notes || '');
    setExtraFiles(savedSelection?.extra_files || []);
    setAddTemplateDropdownId('');
  }, [isOpen, claimRow, templates, savedSelection]);

  // Resolved template objects currently selected
  const activeSelectedTemplates = useMemo(() => {
    const reasonMap = {};
    (matchResult.matchedTemplates || []).forEach((m) => {
      reasonMap[m.id] = m.matchReasons || [];
    });
    return selectedTemplateIds
      .map((id) => {
        const found = templates.find((t) => t.id === id);
        if (!found) return null;
        return {
          ...found,
          matchReasons: reasonMap[id] || ['Manually Selected Override'],
          isAutoMatched: Boolean(reasonMap[id]),
        };
      })
      .filter(Boolean);
  }, [selectedTemplateIds, templates, matchResult.matchedTemplates]);

  // Auto-hydrate and merge template text unless the user manually edited the textarea
  useEffect(() => {
    if (!claimRow || isTextManuallyEdited) return;
    const blocks = activeSelectedTemplates.map((tpl) => {
      const hydrated = hydrateTemplateText(tpl.template_text, claimRow, selectedProcedure);
      return `=== [TEMPLATE: ${tpl.name}] ===\n${hydrated}`;
    });
    setCompiledText(blocks.join('\n\n'));
  }, [activeSelectedTemplates, claimRow, selectedProcedure, isTextManuallyEdited]);

  const handleProcedureChange = (newProc) => {
    setSelectedProcedure(newProc);
    const nextMatch = matchTemplatesForClaim(claimRow, templates, newProc);
    setSelectedTemplateIds(nextMatch.matchedTemplates.map((t) => t.id));
    setIsTextManuallyEdited(false);
  };

  const handleToggleTemplate = (tplId) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(tplId) ? prev.filter((id) => id !== tplId) : [...prev, tplId]
    );
    setIsTextManuallyEdited(false);
  };

  const handleAddTemplateFromDropdown = (tplId) => {
    if (!tplId) return;
    if (!selectedTemplateIds.includes(tplId)) {
      setSelectedTemplateIds((prev) => [tplId, ...prev]);
      setIsTextManuallyEdited(false);
    }
    setAddTemplateDropdownId('');
  };

  const handleExtraFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = files.map((file) => ({
      id: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: file.name,
      size_kb: Math.max(1, Math.round(file.size / 1024)),
      role: 'Case Supplement',
    }));
    setExtraFiles((prev) => [...prev, ...added]);
    if (extraFileInputRef.current) {
      extraFileInputRef.current.value = '';
    }
    toast({
      title: 'Supplemental File Attached',
      description: `Attached ${files.map((f) => f.name).join(', ')} to this appeal.`,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleConfirm = () => {
    if (!claimRow) return;
    const customInstructions = buildAppealJobCustomInstructions({
      selectedTemplates: activeSelectedTemplates,
      procedureType: selectedProcedure,
      submissionChannel,
      compiledTemplateText: compiledText,
      extraFiles,
      billerNotes,
    });

    const selectionMeta = {
      claim_number: claimRow.claim_number,
      procedure_type: selectedProcedure,
      submission_channel: submissionChannel,
      template_ids: selectedTemplateIds,
      template_names: activeSelectedTemplates.map((t) => t.name),
      attached_files_count:
        activeSelectedTemplates.reduce((acc, t) => acc + (t.attached_files?.length || 0), 0) +
        extraFiles.length,
      extra_files: extraFiles,
      biller_notes: billerNotes,
    };

    onConfirmLaunch({
      claimRow,
      customInstructions,
      selectionMeta,
      force: forceRegenerate,
    });
  };

  if (!claimRow) return null;

  const availableToAdd = (templates || []).filter((t) => !selectedTemplateIds.includes(t.id));
  const deniedCpts =
    claimRow.denied_core_cpts && claimRow.denied_core_cpts.length > 0
      ? claimRow.denied_core_cpts
      : claimRow.denied_cpts || [];
  const billedIcds =
    claimRow.billed_icd10_codes && claimRow.billed_icd10_codes.length > 0
      ? claimRow.billed_icd10_codes
      : claimRow.revedy_icd10_codes || [];

  const totalReferenceFiles = [
    ...activeSelectedTemplates.flatMap((t) =>
      (t.attached_files || []).map((f) => ({ ...f, sourceTemplate: t.name }))
    ),
    ...extraFiles.map((f) => ({ ...f, sourceTemplate: 'One-Off Claim Attachment' })),
  ];

  const activeChannelObj =
    SUBMISSION_CHANNEL_OPTIONS.find((c) => c.value === submissionChannel) ||
    SUBMISSION_CHANNEL_OPTIONS[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="92vh">
        <ModalHeader bg="gray.800" color="white" borderTopRadius="md" py={3.5}>
          <Flex justify="space-between" align="center" pr={8} wrap="wrap" gap={2}>
            <Box>
              <HStack spacing={2.5} wrap="wrap">
                <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5}>
                  PRE-FLIGHT TEMPLATE REVIEW
                </Badge>
                <Text fontSize="md" fontWeight="bold">
                  {forceRegenerate ? 'Regenerate Appeal / eAppeal:' : 'Create Appeal / Kick Off eAppeal:'}{' '}
                  {claimRow.claim_number}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                  {claimRow.payer_name}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.300" fontWeight="normal" mt={1}>
                Review the auto-matched payer &amp; procedure templates below, swap or customize if needed, and launch the appeal job.
              </Text>
            </Box>

            {onSwitchToTemplatesTab && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="teal"
                color="teal.200"
                borderColor="teal.400"
                _hover={{ bg: 'gray.700' }}
                leftIcon={<EditIcon />}
                onClick={() => {
                  onClose();
                  onSwitchToTemplatesTab();
                }}
              >
                Open Template Library
              </Button>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody py={4} bg="gray.50">
          <VStack spacing={4} align="stretch">
            {/* 1. Claim Context, Procedure Detection & Submission Channel */}
            <Card variant="outline" bg="white" borderTop="3px solid #3182CE">
              <CardBody py={3} px={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Claim / Patient / DOS
                    </Text>
                    <Text fontSize="xs" fontWeight="bold" color="gray.800">
                      {claimRow.claim_number} (Case #{claimRow.nma_case_no}) • {claimRow.patient_name || 'N/A'}
                    </Text>
                    <Text fontSize="11px" color="gray.600">
                      DOS: {claimRow.date_of_service || 'N/A'} • POS {claimRow.place_of_service || 'N/A'} •{' '}
                      {claimRow.facility_name || 'Facility'}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Disputed CPTs &amp; Remittance
                    </Text>
                    <HStack spacing={1} wrap="wrap" mt={0.5}>
                      {deniedCpts.map((cpt) => (
                        <Badge key={cpt} colorScheme="red" fontSize="10px">
                          {cpt}
                        </Badge>
                      ))}
                      {claimRow.top_carc && (
                        <Badge colorScheme="orange" fontSize="10px">
                          {claimRow.top_carc}
                          {claimRow.top_rarc ? ` / ${claimRow.top_rarc}` : ''}
                        </Badge>
                      )}
                      <Text fontSize="xs" fontWeight="bold" color="red.600" ml={1}>
                        ({formatCurrency(claimRow.core_denied_amount)})
                      </Text>
                    </HStack>
                    <Text fontSize="11px" color="gray.600" noOfLines={1} mt={0.5} title={claimRow.primary_denial_reason}>
                      {claimRow.primary_denial_reason || 'Denied per payer remittance'}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Billed ICD-10 Diagnoses
                    </Text>
                    <HStack spacing={1} wrap="wrap" mt={0.5}>
                      {billedIcds.length > 0 ? (
                        billedIcds.slice(0, 6).map((dx, i) => (
                          <Badge key={dx} colorScheme={i === 0 ? 'blue' : 'gray'} fontSize="10px">
                            {String.fromCharCode(65 + i)}: {dx}
                          </Badge>
                        ))
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          See Case Record
                        </Text>
                      )}
                    </HStack>
                    <Text fontSize="10px" color="green.700" mt={1}>
                      {matchResult.inferredProcedure.reason}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Divider mb={3} />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700">
                        Detected Procedure Type (Change to Re-Match Templates)
                      </Text>
                      <Badge colorScheme="green" variant="subtle" fontSize="10px">
                        Auto-Detected: {matchResult.inferredProcedure.shortLabel}
                      </Badge>
                    </HStack>
                    <Select
                      size="sm"
                      bg="blue.50"
                      borderColor="blue.300"
                      fontWeight="semibold"
                      value={selectedProcedure}
                      onChange={(e) => handleProcedureChange(e.target.value)}
                    >
                      {PROCEDURE_TYPE_OPTIONS.map((proc) => (
                        <option key={proc} value={proc}>
                          {proc}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700">
                        Appeal / eAppeal Submission Mode
                      </Text>
                      <Badge colorScheme={activeChannelObj.color} fontSize="10px">
                        {activeChannelObj.badge}
                      </Badge>
                    </HStack>
                    <Select
                      size="sm"
                      value={submissionChannel}
                      onChange={(e) => setSubmissionChannel(e.target.value)}
                    >
                      {SUBMISSION_CHANNEL_OPTIONS.map((ch) => (
                        <option key={ch.value} value={ch.value}>
                          {ch.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* 2. Auto-Matched Templates & Override Selector */}
            <Card variant="outline" bg="white">
              <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <HStack spacing={2}>
                    <Heading size="xs" color="gray.800" textTransform="uppercase">
                      2. Templates Selected for This Appeal ({activeSelectedTemplates.length})
                    </Heading>
                    <Badge colorScheme="green" fontSize="10px">
                      Auto-Matched by Payer + Procedure + CPT
                    </Badge>
                  </HStack>

                  {/* Add / Swap Another Template Dropdown */}
                  <HStack spacing={2}>
                    <Select
                      size="xs"
                      w="290px"
                      bg="white"
                      placeholder="+ Add / Swap Another Template from Library..."
                      value={addTemplateDropdownId}
                      onChange={(e) => handleAddTemplateFromDropdown(e.target.value)}
                    >
                      {availableToAdd.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.payer_match} • {t.procedure_type.split(' (')[0]})
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </Flex>
              </CardHeader>

              <CardBody py={3} px={4}>
                {activeSelectedTemplates.length === 0 ? (
                  <Alert status="warning" borderRadius="md" fontSize="xs">
                    <AlertIcon />
                    No templates are currently checked. Select a template from the dropdown above or type custom instructions below.
                  </Alert>
                ) : (
                  <VStack align="stretch" spacing={2.5}>
                    {activeSelectedTemplates.map((tpl) => (
                      <Box
                        key={tpl.id}
                        p={3}
                        bg={tpl.is_base_template ? 'teal.50' : 'blue.50'}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={tpl.is_base_template ? 'teal.200' : 'blue.200'}
                      >
                        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
                          <HStack align="flex-start" spacing={2.5} flex="1">
                            <Checkbox
                              colorScheme="blue"
                              mt={0.5}
                              isChecked={selectedTemplateIds.includes(tpl.id)}
                              onChange={() => handleToggleTemplate(tpl.id)}
                            />
                            <Box flex="1">
                              <HStack spacing={2} wrap="wrap" mb={0.5}>
                                <Text fontSize="xs" fontWeight="bold" color="gray.900">
                                  {tpl.name}
                                </Text>
                                {(tpl.matchReasons || []).map((reason) => (
                                  <Badge
                                    key={reason}
                                    colorScheme={tpl.isAutoMatched ? 'green' : 'purple'}
                                    variant="solid"
                                    fontSize="9px"
                                  >
                                    ✓ {reason}
                                  </Badge>
                                ))}
                              </HStack>
                              <Text fontSize="11px" color="gray.700" mb={1.5}>
                                {tpl.description}
                              </Text>

                              {(tpl.attached_files || []).length > 0 && (
                                <HStack spacing={2} wrap="wrap">
                                  <Text fontSize="10px" fontWeight="bold" color="gray.600">
                                    Included Files:
                                  </Text>
                                  {tpl.attached_files.map((f) => (
                                    <Badge
                                      key={f.id || f.name}
                                      colorScheme="blue"
                                      variant="outline"
                                      bg="white"
                                      fontSize="10px"
                                      px={1.5}
                                    >
                                      <AttachmentIcon mr={1} boxSize={2.5} />
                                      {f.name} ({f.role})
                                    </Badge>
                                  ))}
                                </HStack>
                              )}
                            </Box>
                          </HStack>

                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleToggleTemplate(tpl.id)}
                          >
                            Remove
                          </Button>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                )}

                {/* Supplemental Reference Files & Case Packet Strip */}
                <Flex
                  mt={3}
                  pt={2.5}
                  borderTop="1px solid"
                  borderColor="gray.100"
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  gap={2}
                >
                  <HStack spacing={1.5} wrap="wrap">
                    <Text fontSize="11px" fontWeight="bold" color="gray.600">
                      Auto-Bundled Case Clinical Docs:
                    </Text>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Operative Report
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Signed IONM Pro Summary
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ IONM Tech Log / Timestamps
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Hospital Face Sheet
                    </Badge>
                    {extraFiles.map((ef) => (
                      <Badge key={ef.id} colorScheme="purple" variant="solid" fontSize="10px">
                        📎 {ef.name}
                      </Badge>
                    ))}
                  </HStack>

                  <Box>
                    <input
                      ref={extraFileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md"
                      style={{ display: 'none' }}
                      onChange={handleExtraFileUpload}
                    />
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      leftIcon={<AttachmentIcon />}
                      onClick={() => extraFileInputRef.current && extraFileInputRef.current.click()}
                    >
                      + Attach One-Off File for This Claim
                    </Button>
                  </Box>
                </Flex>
              </CardBody>
            </Card>

            {/* 3. Hydrated Template Preview & Inline Override Editor */}
            <Card variant="outline" bg="white">
              <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <Box>
                    <Heading size="xs" color="gray.800" textTransform="uppercase">
                      3. Hydrated Template Instructions &amp; Boilerplate Preview (Editable)
                    </Heading>
                    <Text fontSize="11px" color="gray.500">
                      Placeholders have been populated with {claimRow.claim_number}&apos;s data. You can edit the text directly before launching.
                    </Text>
                  </Box>
                  {isTextManuallyEdited && (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="gray"
                      leftIcon={<RepeatIcon />}
                      onClick={() => setIsTextManuallyEdited(false)}
                    >
                      Reset to Template Default
                    </Button>
                  )}
                </Flex>
              </CardHeader>

              <CardBody py={3} px={4}>
                <VStack spacing={3} align="stretch">
                  <Textarea
                    rows={10}
                    fontSize="xs"
                    fontFamily="mono"
                    bg="gray.900"
                    color="gray.100"
                    p={3}
                    borderRadius="md"
                    value={compiledText}
                    onChange={(e) => {
                      setCompiledText(e.target.value);
                      setIsTextManuallyEdited(true);
                    }}
                  />

                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                      Additional Claim-Specific Biller Notes (Optional)
                    </Text>
                    <Input
                      size="sm"
                      placeholder="e.g., Emphasize Dr. Smith's intraoperative alert at 10:42 AM or reference prior auth #..."
                      value={billerNotes}
                      onChange={(e) => setBillerNotes(e.target.value)}
                    />
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200">
          <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap={2}>
            <Text fontSize="xs" color="gray.600">
              <b>{activeSelectedTemplates.length}</b> template(s) &amp;{' '}
              <b>{totalReferenceFiles.length}</b> policy/sample reference file(s) will be applied to{' '}
              <b>{claimRow.claim_number}</b>.
            </Text>
            <HStack spacing={2.5}>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme={submissionChannel === 'PAYER_PORTAL_EAPPEAL' ? 'purple' : 'blue'}
                leftIcon={forceRegenerate ? <RepeatIcon /> : <CheckCircleIcon />}
                isLoading={isSubmitting}
                onClick={handleConfirm}
              >
                {submissionChannel === 'PAYER_PORTAL_EAPPEAL'
                  ? 'Confirm Templates & Kick Off eAppeal'
                  : forceRegenerate
                  ? 'Confirm Templates & Regenerate Appeal'
                  : 'Confirm Templates & Create Appeal Job'}
              </Button>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NMAAppealLaunchModal;
